import get from 'lodash/get'

import { StructureObjectDto } from '@/endpoints/models'
import { store } from '@/store'
import { initFolder } from '@/store/modules/folder/actions'
import { initSystem } from '@/store/modules/system/actions'
import { AppDispatch } from '@/store/utils'
import { GenerateCodeEnum, NamingTypeEnum } from '@/types'
import { nextFrame } from '@/utils/async'

const PATHS = {
	[NamingTypeEnum.GENERATE_CODE]: 'form.generateCode',
	[NamingTypeEnum.GENERATE_TABLE_COLUMN_CODE]: 'form.generateTableColumnCode',
}

interface GetCanGenerateNaming {
	generateColumnCode: boolean
	generateElementCode: boolean
	isFormField?: boolean
}

/** Check parent folders & system if the node can auto generate naming */
export const getCanGenerateNaming = async (
	nodeId: number,
	namingType?: NamingTypeEnum,
	isThisFolder?: boolean,
): Promise<GetCanGenerateNaming> => {
	const { folders } = store.getState().folder
	const { systems } = store.getState().system
	const { nodes } = store.getState().node
	const dispatch = store.dispatch as AppDispatch
	const parentId = nodes[nodeId]?.parentStructureId

	let generateElementCodeValue: GenerateCodeEnum | undefined = undefined
	let generateColumnCodeValue: GenerateCodeEnum | undefined = undefined

	if (parentId === undefined) {
		return {
			generateElementCode: false,
			generateColumnCode: false,
		}
	}

	const parentNode = nodes[parentId]

	if (isThisFolder) {
		generateElementCodeValue = get(
			folders[nodeId],
			PATHS[NamingTypeEnum.GENERATE_CODE],
		)
		generateColumnCodeValue = get(
			folders[nodeId],
			PATHS[NamingTypeEnum.GENERATE_TABLE_COLUMN_CODE],
		)

		// Fallback to parent system if values are undefined or inherit
		if (
			generateElementCodeValue === GenerateCodeEnum.INHERIT ||
			!generateElementCodeValue
		) {
			generateElementCodeValue = get(
				systems[parentId],
				PATHS[NamingTypeEnum.GENERATE_CODE],
			)
		}
		if (
			generateColumnCodeValue === GenerateCodeEnum.INHERIT ||
			!generateColumnCodeValue
		) {
			generateColumnCodeValue = get(
				systems[parentId],
				PATHS[NamingTypeEnum.GENERATE_TABLE_COLUMN_CODE],
			)
		}
	}

	if (!isThisFolder) {
		switch (parentNode?.type) {
			case StructureObjectDto.TypeEnum.FOLDER: {
				if (folders[parentId] === undefined) {
					await dispatch(initFolder({ nodeId: parentId, editMode: false }))
					await nextFrame()

					return await getCanGenerateNaming(nodeId, namingType, isThisFolder)
				}

				generateElementCodeValue = get(
					folders[parentId],
					PATHS[NamingTypeEnum.GENERATE_CODE],
				)
				generateColumnCodeValue = get(
					folders[parentId],
					PATHS[NamingTypeEnum.GENERATE_TABLE_COLUMN_CODE],
				)

				break
			}

			case StructureObjectDto.TypeEnum.SYSTEM: {
				if (systems[parentId] === undefined) {
					await dispatch(initSystem({ nodeId: parentId, editMode: false }))
					await nextFrame()

					return await getCanGenerateNaming(nodeId, namingType, isThisFolder)
				}

				generateElementCodeValue = get(
					systems[parentId],
					PATHS[NamingTypeEnum.GENERATE_CODE],
				)
				generateColumnCodeValue = get(
					systems[parentId],
					PATHS[NamingTypeEnum.GENERATE_TABLE_COLUMN_CODE],
				)

				break
			}
		}
	}

	// Helper function to process generate code values
	const processGenerateCodeValue = async (
		value: GenerateCodeEnum | undefined,
		propertyName: 'generateElementCode' | 'generateColumnCode',
	): Promise<boolean> => {
		if (value === GenerateCodeEnum.YES) {
			return true
		}

		if (value === GenerateCodeEnum.NO) {
			return false
		}

		if (value === undefined || value === GenerateCodeEnum.INHERIT) {
			const parentResult = await getCanGenerateNaming(
				parentId,
				namingType,
				isThisFolder,
			)
			return parentResult[propertyName]
		}

		return false
	}

	// Process both values
	const generateElementCode = await processGenerateCodeValue(
		generateElementCodeValue,
		'generateElementCode',
	)
	const generateColumnCode = await processGenerateCodeValue(
		generateColumnCodeValue,
		'generateColumnCode',
	)

	return { generateElementCode, generateColumnCode }
}
